import {
  DEFAULT_DEPARTURE_CITY,
  DEFAULT_DEPARTURE_CITY_TYPE,
  DEFAULT_DEPARTURE_CODE,
} from '../constants/appConstant';
import { DEFAULT_ROOM_LISTING, WEEKDAYS } from '../constants/packageConstants';
import { getMMT_HLD_OBJ } from './appUtils';
import { getISTTime } from './packageUtils';

const INCLUSIONS = {
  HOTELS: 'Hotels',
  ACTIVITIES: 'Activities',
  TRANSFERS: 'Transfers',
  FLIGHTS: 'Flights',
};

export const priceFormatter = () =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumSignificantDigits: 1,
  });

export const formatPrice = priceFormatter().format;

export const formatNumberWithComma = new Intl.NumberFormat('en-IN').format;

export const base64ToArrayBuffer = base64 => {
  const binaryString = window.atob(base64);
  const binaryLen = binaryString.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i++) {
    const ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }
  return bytes;
};

export const getDeviceTimezoneAndLocale = () => {
  let timeZone = null;
  let locale = null;

  try {
    const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
    timeZone = resolvedOptions.timeZone || null;
    locale = resolvedOptions.locale || null;
  } catch (error) {
    locale = typeof navigator !== 'undefined' ? navigator?.language || null : null;
  }

  return { timeZone, locale };
};

export const formatNumber = n => (n < 10 ? `0${n}` : n);

export const formatTime = (time = '') => {
  const timeAndTimezoneStr = time?.split('T')[1];
  const timeStr = timeAndTimezoneStr?.split('+')[0] || '';
  const timeArr = timeStr?.split(':') || [];
  const hours = timeArr[0];
  const minutes = timeArr[1];
  if (hours && minutes) {
    return `${hours}:${minutes}`;
  }
  return '';
};

export const formatDuration = (duration, showFullDetail = false) => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  const h = showFullDetail ? '' : 'h';
  const m = showFullDetail ? '' : 'm';
  return hours > 0
    ? `${formatNumber(hours)}${h} ${formatNumber(minutes)}${m}`
    : `${formatNumber(minutes)}${m}`;
};

export const getMonth = month => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return months[month - 1];
};

export const increamentDays = (date, count) => {
  const copyDate = new Date(date);
  copyDate.setDate(date.getDate() + count);
  return copyDate;
};

export const getMilliSec = dateStr => {
  const [year, month, date] = dateStr.split('-');
  const dateObj = new Date(Number(year), Number(month) - 1, Number(date));
  return dateObj.getTime();
};

export const truncateString = (str = '', len) => {
  if (str.length < len) {
    return str;
  }
  return `${str.substring(0, len - 2).trim()}...`;
};
export const formatDateFromStr = date => {
  // Format: yyyy-mm-dd
  const dateSplitArr = date.split('-');
  const selectedDate = new Date(
    Number(dateSplitArr[0]),
    Number(dateSplitArr[1]) - 1,
    Number(dateSplitArr[2])
  );
  return selectedDate;
};

export const deepCopy = object =>
  // to be used when object is not to be passed as a reference rather as a copy of the same.
  JSON.parse(JSON.stringify(object));

export const formatDestinationMeta = destinationMeta => {
  const { departureCity = {}, destinationCities = [] } = destinationMeta;
  const toCityArray = [];
  const toCityCodeArray = [];
  destinationCities.forEach(destination => {
    if (destination?.cityName) toCityArray.push(destination?.cityName);
    if (destination?.locusDetails?.locusCode)
      toCityCodeArray.push(destination?.locusDetails?.locusCode);
  });
  return {
    toCity: toCityArray.join(',') || '',
    toCityCode: toCityCodeArray.length ? toCityCodeArray.join(',') : '',
    fromCity: departureCity?.cityName || DEFAULT_DEPARTURE_CITY,
    fromCityCode: departureCity?.locusDetails?.locusCode || DEFAULT_DEPARTURE_CODE,
    fromCityType: departureCity?.locusDetails?.locusType || DEFAULT_DEPARTURE_CITY_TYPE,
    destinationCities,
  };
};

export const getDestinationCityPayload = appliedCriteria => {
  const { destinationCities } = appliedCriteria;
  const locusDetailsArray = [];
  destinationCities.forEach(destination => {
    locusDetailsArray.push({
      locus_code: destination?.locusDetails?.locusCode,
      locus_type: destination?.locusDetails?.locusType,
      locus_city_code: null,
    });
  });
  return locusDetailsArray;
};

export const formatOfferSectionData = (
  couponName,
  discountAmount,
  message,
  selected,
  couponExpiryDate,
  couponEndTime,
  recommendationMessage
) => ({
  name: couponName,
  couponCode: couponName,
  discountAmount,
  description: message,
  selected,
  couponExpiryDate,
  couponEndTime,
  recommendationMessage,
});

export const convertRoomForUrl = (rooms = DEFAULT_ROOM_LISTING) => {
  const roomStrs = [];
  rooms.map(room => {
    const {
      noOfAdults = '',
      noOfChildrenWB = '',
      noOfChildrenWOB = '',
      noOfInfants = '',
      listOfAgeOfChildrenWB = [],
      listOfAgeOfChildrenWOB = [],
      listOfInfAge = [],
    } = room;
    const str =
      `${noOfAdults ?? 0},${noOfChildrenWB ?? 0},${noOfChildrenWOB ?? 0},${noOfInfants ?? 0}` +
      `,${listOfAgeOfChildrenWB.join('-')},${listOfAgeOfChildrenWOB.join('-')},${listOfInfAge.join(
        '-'
      )}`;
    roomStrs.push(str);
  });
  return roomStrs.join('|');
};

export const getRoomFromParams = (roomStr = '') => {
  const roomStrArr = roomStr.split('|');
  const rooms = [];
  roomStrArr.map(strRoom => {
    const [
      noOfAdults,
      noOfChildrenWB,
      noOfChildrenWOB,
      noOfInfants,
      listOfAgeOfChildrenWB,
      listOfAgeOfChildrenWOB,
      listOfInfAge,
    ] = strRoom.split(',');
    rooms.push({
      noOfAdults: parseInt(noOfAdults),
      noOfChildrenWB: parseInt(noOfChildrenWB),
      noOfChildrenWOB: parseInt(noOfChildrenWOB),
      noOfInfants: parseInt(noOfInfants),
      listOfAgeOfChildrenWB: listOfAgeOfChildrenWB
        ? listOfAgeOfChildrenWB.split('-').map(v => parseInt(v))
        : [],
      listOfAgeOfChildrenWOB: listOfAgeOfChildrenWOB
        ? listOfAgeOfChildrenWOB.split('-').map(v => parseInt(v))
        : [],
      listOfInfAge: listOfInfAge ? listOfInfAge.split('-').map(v => parseInt(v)) : [],
    });
  });
  return rooms;
};

const BRANCH = {
  DOM: 'domestic',
  OBT: 'outbound',
};
export const getOmniturePageName = pageName => {
  const branchName = BRANCH[getMMT_HLD_OBJ()?.branch] || '';
  return `funnel:in ${branchName} holidays:${pageName}`;
};

export const getDateString = date => {
  const formatDate = new Date(getISTTime(date));
  return `${WEEKDAYS[formatDate.getDay()]}, ${formatNumber(formatDate.getDate())} ${getMonth(
    formatDate.getMonth() + 1
  )}`;
};

export const getFormatedPaxCount = (rooms = []) => {
  let childrenCount = 0;
  let adultCount = 0;

  rooms?.forEach?.(r => {
    const room = r || {};
    childrenCount +=
      (room.noOfChildrenWB ?? 0) + (room.noOfChildrenWOB ?? 0) + (room.noOfInfants ?? 0);
    adultCount += room?.noOfAdults;
  });

  let returnString = `${adultCount} ${adultCount === 1 ? 'Adult' : 'Adults'}`;

  if (childrenCount) {
    returnString += `, ${childrenCount} ${childrenCount === 1 ? 'Child' : 'Children'}`;
  }

  return returnString;
};

export const getFormatedInclusions = (packageInclusionsDetail = {}, delimeter = ', ') => {
  const inclusionsArray = [];
  if (packageInclusionsDetail) {
    if (packageInclusionsDetail?.hotels) {
      inclusionsArray.push(INCLUSIONS.HOTELS);
    }
    if (packageInclusionsDetail?.flights) {
      inclusionsArray.push(INCLUSIONS.FLIGHTS);
    }
    if (packageInclusionsDetail?.airportTransfers || packageInclusionsDetail?.carItinerary) {
      inclusionsArray.push(INCLUSIONS.TRANSFERS);
    }
    if (packageInclusionsDetail?.activities) {
      inclusionsArray.push(INCLUSIONS.ACTIVITIES);
    }

    if (inclusionsArray.length) {
      return inclusionsArray.join(delimeter);
    }

    return '';
  }
};

export const getTimeSlotDuration = (startTime, endtime) => {
  const startTimeArr = startTime?.split(':');
  const EndTimeArr = endtime?.split(':');
  const minutesCalc = Number(EndTimeArr[1]) - Number(startTimeArr[1]);
  const hoursCalc = Number(EndTimeArr[0]) - Number(startTimeArr[0]);
  return (minutesCalc + hoursCalc * 60) / 60 > 1
    ? ((minutesCalc + hoursCalc * 60) / 60).toFixed(2)
    : (minutesCalc + hoursCalc * 60) / 60;
};
